/**
 * Purchased Any Trigger Component
 *
 * Handles purchase triggers for Plan action where reminderType stores
 * the sellable type directly ('all', 'workout_plan', 'group', 'product', 'package', 'client')
 *
 * For 'all': "purchases anything"
 * For specific types with items: shows actual item names
 * For specific types without items: "purchases any plan(s)" etc.
 */

import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { fetchAllStripePlans } from 'src/models/payment/payment';
import {
  SellableType,
  useSellableItems,
} from 'src/shared/Forms/Selects/SellableItemsMultiSelect';

import { TriggerComponentProps } from '../automationTriggers';
import { GroupOneTimePurchaseField } from '../components/GroupOneTimePurchaseField';
import { SellableTypeSelect } from '../components/SellableTypeSelect';
import { StripePlanFields } from '../components/StripePlanFields';

const PURCHASE_ANY_TRIGGERS = new Set(['all', 'any', 'workout_plan', 'group', 'product', 'package', 'client']);

// Map trigger/reminderType to display labels and sellable types
const TYPE_CONFIG: Record<
  string,
  { label: string; sellableType?: SellableType }
> = {
  all: { label: 'anything' },
  any: { label: 'anything' }, // 'any' is alias used by Assessment
  workout_plan: { label: 'plan(s)', sellableType: 'workout_plan' },
  group: { label: 'group(s)', sellableType: 'group' },
  product: { label: 'product(s)', sellableType: 'product' },
  package: { label: 'package(s)', sellableType: 'package' },
  client: { label: 'client training' },
};

export const PurchasedAnyTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { user } = useSession();
  const triggerKey = automation.reminderType || 'all';
  const config = TYPE_CONFIG[triggerKey] || { label: triggerKey };
  const showSellableTypeSelect = PURCHASE_ANY_TRIGGERS.has(triggerKey);
  const clientTrainingLabel = `${t('trainer.client.one').toLowerCase()} training`;

  // Get selected item IDs
  const selectedItemIds = Array.isArray(automation.reminderTypeId)
    ? automation.reminderTypeId
    : automation.reminderTypeId
      ? [automation.reminderTypeId]
      : [];
  const hasSelectedItems = selectedItemIds.length > 0;

  // Fetch sellable items to display names (only when we have selected items)
  const { data: items = [] } = useSellableItems(
    hasSelectedItems && config.sellableType ? config.sellableType : null
  );

  // Find selected item names
  const selectedItemNames = items
    .filter((item) =>
      selectedItemIds.some((id) => String(id) === String(item.value))
    )
    .map((item) => item.label);

  // Fetch Stripe plans for readonly display
  const { data: stripePlans = [] } = useQuery({
    queryKey: ['stripePlans', user?.id],
    queryFn: () => fetchAllStripePlans(),
    enabled: readOnly && !!automation.stripePlan?.id,
    staleTime: 60000,
  });

  if (readOnly) {
    const parts: string[] = [];

    // Build the main trigger text
    if (hasSelectedItems && selectedItemNames.length > 0) {
      // Show actual item names: "purchases Plan A, Plan B"
      parts.push(`purchases ${selectedItemNames.join(', ')}`);
    } else if (triggerKey === 'all' || triggerKey === 'any') {
      // "purchases anything"
      parts.push(`purchases ${config.label}`);
    } else if (triggerKey === 'client') {
      parts.push(`purchases any ${clientTrainingLabel}`);
    } else {
      // "purchases any plan(s)"
      parts.push(`purchases any ${config.label}`);
    }

    // Add payment plan if selected (stripePlan is { id, nickname })
    if (automation.stripePlan?.id) {
      const plan = stripePlans.find((p) => p.id === automation.stripePlan?.id);
      if (plan) {
        parts.push(`with Payment Plan: ${plan.name}`);
      } else if (automation.stripePlan.nickname) {
        parts.push(`with Payment Plan: ${automation.stripePlan.nickname}`);
      }
    }

    if (automation.afterTrial) {
      parts.push('(after trial)');
    }

    if (automation.everyPayment) {
      parts.push('(every payment)');
    }

    return <span>{parts.join(' ')}</span>;
  }

  // Edit mode - sellable type dropdown for purchased-any triggers
  return (
    <Stack spacing={2}>
      {showSellableTypeSelect && <SellableTypeSelect index={index} />}
      <GroupOneTimePurchaseField automation={automation} index={index} />
      {!automation.lifeTime && (
        <StripePlanFields automation={automation} index={index} />
      )}
    </Stack>
  );
};
